import {
  Menu,
  Text,
  UnstyledButton,
  Flex,
  Divider,
  Box,
  SegmentedControl,
  Center,
  Indicator,
  Switch,
  ActionIcon,
} from "@mantine/core";
import { IconBell, IconDots, IconMail, IconMailOpened } from "@tabler/icons";
import {useEffect, useRef, useState} from "react";
import Notification from "../../notifications/Notification";
import {listNotifications, markNotificationsRead} from "../../../Api";
import { useAuth0 } from "@auth0/auth0-react";
import { sidebarFooterTargetClass, sidebarFooterLabelClass } from "./utils";

const NotificationsDropdown = ({ sidebarExpanded }: {
  sidebarExpanded: boolean;
}) => {
  const auth0 = useAuth0();
  const [notifications, setNotifications] = useState<any[]>([])
  const refreshFlags = useRef<{ isSoftRefresh: boolean; isHardRefresh: boolean }>({
    isSoftRefresh: false,
    isHardRefresh: false,
  });
  const [opened, setOpened] = useState(false);
  const [marked, setMarked] = useState(false);
  const [tab, setTab] = useState("comments");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const filtered = notifications.filter((n) => n.status === "unread");
  const notificationsCount = filtered.length;

  const getListNotifications = () =>{
    listNotifications(auth0)
      .then((noti: any) => {
        setNotifications(noti);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  useEffect(() => {
    const resources = performance.getEntriesByType("resource");
    const navigation: any = performance.getEntriesByType("navigation")[0];
    const isSoftRefresh = resources.some((res: any) => res.transferSize === 0 && res.responseStart > 0);
    const isHardRefresh = navigation?.type === "reload" && !isSoftRefresh;
    refreshFlags.current = {
      isSoftRefresh: isSoftRefresh,
      isHardRefresh: isHardRefresh,
    };
    getListNotifications();
  }, []);

  const markAllNotificationsRead = async (id = '', mode: string) => {
    await markNotificationsRead(auth0, id, mode).then(() => {
      setMarked(!marked);
    });
  };

  let comments = notifications.filter((n) => n.notification.commentId);
  let activity = notifications.filter((n) => !n.notification.commentId);
  if (showUnreadOnly) {
    comments = comments.filter((n) => n.status === "unread");
    activity = activity.filter((n) => n.status === "unread");
  }
  // Count unread notifications for blue dot indication
  const unreadComments = comments.filter((n) => n.status === "unread").length;
  const unreadActivity = activity.filter((n) => n.status === "unread").length;

  const bellIcon =
    notificationsCount === 0 ? (
      <IconBell color="#4B4BE8" size={20} stroke={2} />
    ) : (
      <Indicator
        size={15}
        color="#FF9499"
        inline
        label={
          <Text p={0} m={0}>
            {notificationsCount}
          </Text>
        }
      >
        <IconBell color="#4B4BE8" size={20} stroke={2} />
      </Indicator>
    );

  return (
    <Menu
      width={400}
      shadow="md"
      position={'left-end'}
      withArrow
      withinPortal
      opened={opened}
      onChange={setOpened}
    >
      <Menu.Target>
        <UnstyledButton className={sidebarFooterTargetClass(sidebarExpanded)}>
          <Flex align="center" w="100%">
            <Box className="sidebar-footer-icon-slot">
              {bellIcon}
            </Box>
            <Text
              ml="md"
              fz="14px"
              mb="1px"
              lineClamp={1}
              className={sidebarFooterLabelClass(sidebarExpanded)}
            >
              Notifications
            </Text>
          </Flex>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown p={0}>
        <Flex justify={"space-between"} className="sidebar-notifications-header" align={"center"} px="xs" >
          <Text
            py={6}
            mb={0}
            className="sidebar-notifications-title"
            fw="bold"
          >
            {" "}
            Notifications{" "}
          </Text>
          <Flex align="center">
            <Box
              className="sidebar-notifications-switch"
              data-switch-on={showUnreadOnly ? "true" : "false"}
            >
              <Switch
                size="xs"
                checked={showUnreadOnly}
                onChange={(event) => setShowUnreadOnly(event.currentTarget.checked)}
                label="Unread"
                mr={15}
              />
            </Box>
            <Menu shadow="md" position="bottom-end" withArrow withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" className="sidebar-notifications-dots-btn" size={24}>
                  <IconDots size={15} color="#1C1B1F"/>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="sidebar-notifications-submenu-dropdown">
                <Menu.Item className="sidebar-notifications-submenu-item" onClick={() => markAllNotificationsRead('', 'viewed')}>{"Mark all as read"}</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
        <Box
          mah={500}
          className="sidebar-notifications-scroll"
        >
          <SegmentedControl
            className="sidebar-notifications-segments"
            value={tab}
            onChange={setTab}
            m="sm"
            fullWidth
            radius="4px"
            data={[
              {
                label: (
                  <Flex gap={8} justify="center" align="center" className="sidebar-notifications-tab-label">
                    {unreadComments > 0 && <Box className="sidebar-notifications-unread-dot" />}
                    Comments{" "}
                  </Flex>
                ),
                value: "comments",
              },
              {
                label: (
                  <Flex gap={8} justify="center" align="center" className="sidebar-notifications-tab-label">
                    {" "}
                    {unreadActivity > 0 && <Box className="sidebar-notifications-unread-dot" />}
                    Activity
                  </Flex>
                ),
                value: "activity",
              }
            ]}
          />
          {((tab === "comments" ? comments : activity).length > 0) ? (
            (tab === "comments" ? comments : activity).map((n, index) => (
              <Box key={n._id}>
                <Notification getListNotifications={getListNotifications} notification={{ ...n.notification, status: n.status }} />
                {index + 1 !== (tab === "comments" ? comments : activity).length && <Divider my="md" />}
              </Box>
            ))
          ) : (
            <Center p="md" mb="md">
              <Box className="sidebar-notifications-empty">
                {tab === "activity" ? <IconMail size={35} color="#5b60e38c" /> : <IconMailOpened size={35} color="#5b60e38c" />}
                <Text color="gray"> No {tab} notifications</Text>
              </Box>
            </Center>
          )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default NotificationsDropdown;
